import { collection, addDoc, getDocs, doc, getDoc, setDoc, deleteDoc, onSnapshot, updateDoc, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { firestore, storage } from '../config/firebase';

const INTERESTS_SUB = 'interests';
const INTEREST_NDA_PREFIX = 'share-interests-nda';
const INTEREST_CESSION_PREFIX = 'share-interests-cession';

function interestsRef(shareToken) {
  return collection(firestore, 'shares', shareToken, INTERESTS_SUB);
}

/**
 * Create a "demande plus d'information" interest for the current user (recipient).
 * @param {string} shareToken
 * @param {string} recipientUid
 * @param {{ recipientEmail?: string, recipientDisplayName?: string, recipientPhone?: string }} [opts]
 * @returns {Promise<string>} interestId
 */
export async function createInterest(shareToken, recipientUid, opts = {}) {
  if (!shareToken || !recipientUid) throw new Error('shareToken and recipientUid required');
  const col = interestsRef(shareToken);
  const phone =
    typeof opts.recipientPhone === 'string' ? opts.recipientPhone.trim().slice(0, 40) : '';
  const docRef = await addDoc(col, {
    recipientUid,
    recipientEmail: opts.recipientEmail || null,
    recipientDisplayName: opts.recipientDisplayName || null,
    recipientPhone: phone || null,
    status: 'requested',
    requestedAt: new Date(),
  });
  return docRef.id;
}

/**
 * Get existing interest for this recipient on this share (if any).
 * @param {string} shareToken
 * @param {string} recipientUid
 * @returns {Promise<{ id: string, ... } | null>}
 */
export async function getInterestForRecipient(shareToken, recipientUid) {
  if (!shareToken || !recipientUid) return null;
  const col = interestsRef(shareToken);
  const q = query(col, where('recipientUid', '==', recipientUid));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const d = snapshot.docs[0];
  return { id: d.id, ...d.data() };
}

/**
 * Subscribe to all interests for a share (for cédant view).
 * @param {string} shareToken
 * @param {(interests: Array<{ id: string, ... }>) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeInterests(shareToken, callback) {
  if (!shareToken) {
    callback([]);
    return () => {};
  }
  return onSnapshot(
    interestsRef(shareToken),
    (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(list);
    },
    (err) => {
      console.warn('subscribeInterests error', err);
      callback([]);
    }
  );
}

/**
 * Upload signed NDA for an interest. Updates interest with ndaSignedFileRef and status nda_submitted.
 * @param {string} shareToken
 * @param {string} interestId
 * @param {File} file
 * @returns {Promise<void>}
 */
export async function submitNda(shareToken, interestId, file) {
  if (!shareToken || !interestId || !file) throw new Error('shareToken, interestId and file required');
  const fileId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  const safeName = (file.name || 'nda-signed.pdf').replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${INTEREST_NDA_PREFIX}/${shareToken}/${interestId}/${fileId}_${safeName}`;
  const storageRefPath = ref(storage, path);
  await uploadBytes(storageRefPath, file);
  const fileRef = { storagePath: path, name: file.name || safeName, uploadedAt: new Date().toISOString() };
  const refDoc = doc(firestore, 'shares', shareToken, INTERESTS_SUB, interestId);
  await updateDoc(refDoc, {
    ndaSignedFileRef: fileRef,
    status: 'nda_submitted',
    ndaSubmittedAt: new Date(),
  });
}

/**
 * Cédant approves NDA. Sets status to nda_approved.
 * @param {string} shareToken
 * @param {string} interestId
 */
export async function approveNda(shareToken, interestId) {
  if (!shareToken || !interestId) return;
  const refDoc = doc(firestore, 'shares', shareToken, INTERESTS_SUB, interestId);
  await updateDoc(refDoc, { status: 'nda_approved', ndaApprovedAt: new Date() });
}

/**
 * Cédant grants document access. Sets status to document_access_granted and creates
 * properties/{propertyId}/documentAccess/{recipientUid} so Firestore rules allow the recipient to read generalDossier.
 * @param {string} shareToken
 * @param {string} interestId
 */
export async function grantDocumentAccess(shareToken, interestId) {
  if (!shareToken || !interestId) return;
  const [shareSnap, interestSnap] = await Promise.all([
    getDoc(doc(firestore, 'shares', shareToken)),
    getDoc(doc(firestore, 'shares', shareToken, INTERESTS_SUB, interestId)),
  ]);
  const propertyId = shareSnap.exists() ? shareSnap.data().propertyId : null;
  const recipientUid = interestSnap.exists() ? interestSnap.data().recipientUid : null;
  if (propertyId && recipientUid) {
    const accessRef = doc(firestore, 'properties', propertyId, 'documentAccess', recipientUid);
    await setDoc(accessRef, { shareToken, grantedAt: new Date() });
  }
  const refDoc = doc(firestore, 'shares', shareToken, INTERESTS_SUB, interestId);
  await updateDoc(refDoc, { status: 'document_access_granted', documentAccessGrantedAt: new Date() });
}

/**
 * Cédant revokes document access. Removes documentAccess doc and sets status back to nda_approved.
 * @param {string} shareToken
 * @param {string} interestId
 */
export async function revokeDocumentAccess(shareToken, interestId) {
  if (!shareToken || !interestId) return;
  const [shareSnap, interestSnap] = await Promise.all([
    getDoc(doc(firestore, 'shares', shareToken)),
    getDoc(doc(firestore, 'shares', shareToken, INTERESTS_SUB, interestId)),
  ]);
  const propertyId = shareSnap.exists() ? shareSnap.data().propertyId : null;
  const recipientUid = interestSnap.exists() ? interestSnap.data().recipientUid : null;
  if (propertyId && recipientUid) {
    const accessRef = doc(firestore, 'properties', propertyId, 'documentAccess', recipientUid);
    try {
      await deleteDoc(accessRef);
    } catch (e) {
      console.warn('revokeDocumentAccess: deleteDoc documentAccess', e);
    }
  }
  const refDoc = doc(firestore, 'shares', shareToken, INTERESTS_SUB, interestId);
  await updateDoc(refDoc, { status: 'nda_approved', documentAccessGrantedAt: null });
}

/**
 * Après acceptation d’une offre sur un mandat courtier : retire l’accès dossier et marque l’interest hors course.
 */
export async function revokeMandatedListingParticipant(shareToken, interestId) {
  if (!shareToken || !interestId) return;
  const [shareSnap, interestSnap] = await Promise.all([
    getDoc(doc(firestore, 'shares', shareToken)),
    getDoc(doc(firestore, 'shares', shareToken, INTERESTS_SUB, interestId)),
  ]);
  const propertyId = shareSnap.exists() ? shareSnap.data().propertyId : null;
  const recipientUid = interestSnap.exists() ? interestSnap.data().recipientUid : null;
  if (propertyId && recipientUid) {
    const accessRef = doc(firestore, 'properties', propertyId, 'documentAccess', recipientUid);
    try {
      await deleteDoc(accessRef);
    } catch (e) {
      console.warn('revokeMandatedListingParticipant: deleteDoc documentAccess', e);
    }
  }
  const refDoc = doc(firestore, 'shares', shareToken, INTERESTS_SUB, interestId);
  await updateDoc(refDoc, {
    status: 'listing_access_revoked',
    documentAccessGrantedAt: null,
    listingAccessRevokedAt: new Date(),
  });
}

/**
 * Cédant refuses document access request. Ensures documentAccess is removed and sets status to document_access_refused.
 * Recipient will see the refusal and cannot re-request from their side; owner must grant access later if desired.
 * @param {string} shareToken
 * @param {string} interestId
 */
export async function refuseDocumentAccess(shareToken, interestId) {
  if (!shareToken || !interestId) return;
  const [shareSnap, interestSnap] = await Promise.all([
    getDoc(doc(firestore, 'shares', shareToken)),
    getDoc(doc(firestore, 'shares', shareToken, INTERESTS_SUB, interestId)),
  ]);
  const propertyId = shareSnap.exists() ? shareSnap.data().propertyId : null;
  const recipientUid = interestSnap.exists() ? interestSnap.data().recipientUid : null;
  if (propertyId && recipientUid) {
    const accessRef = doc(firestore, 'properties', propertyId, 'documentAccess', recipientUid);
    try {
      await deleteDoc(accessRef);
    } catch (e) {
      // Ignore missing doc
    }
  }
  const refDoc = doc(firestore, 'shares', shareToken, INTERESTS_SUB, interestId);
  await updateDoc(refDoc, {
    status: 'document_access_refused',
    documentAccessGrantedAt: null,
    documentAccessRefusedAt: new Date(),
  });
}

/**
 * Recipient confirms interest. Sets status to interest_confirmed.
 * @param {string} shareToken
 * @param {string} interestId
 */
export async function confirmInterest(shareToken, interestId) {
  if (!shareToken || !interestId) return;
  const refDoc = doc(firestore, 'shares', shareToken, INTERESTS_SUB, interestId);
  await updateDoc(refDoc, { status: 'interest_confirmed', interestConfirmedAt: new Date() });
}

/**
 * Recipient submits cession contract. Uploads file and sets status to cession_submitted.
 * @param {string} shareToken
 * @param {string} interestId
 * @param {File} file
 */
export async function submitCessionContract(shareToken, interestId, file) {
  if (!shareToken || !interestId || !file) throw new Error('shareToken, interestId and file required');
  const fileId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  const safeName = (file.name || 'cession.pdf').replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${INTEREST_CESSION_PREFIX}/${shareToken}/${interestId}/${fileId}_${safeName}`;
  const storageRefPath = ref(storage, path);
  await uploadBytes(storageRefPath, file);
  const fileRef = { storagePath: path, name: file.name || safeName, uploadedAt: new Date().toISOString() };
  const refDoc = doc(firestore, 'shares', shareToken, INTERESTS_SUB, interestId);
  await updateDoc(refDoc, {
    cessionFileRef: fileRef,
    status: 'cession_submitted',
    cessionSubmittedAt: new Date(),
  });
}

/**
 * Get download URL for a file ref (e.g. NDA or cession contract).
 * @param {string} storagePath
 */
export async function getShareInterestFileDownloadUrl(storagePath) {
  if (!storagePath) throw new Error('storagePath required');
  const r = ref(storage, storagePath);
  return getDownloadURL(r);
}
